import React, { useRef, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import {
  FaTrash,
  FaEdit,
  FaPalette,
  FaThumbtack,
  FaFolderOpen,
  FaFolderPlus,
} from "react-icons/fa";
import { ResourceGroup, ResourceType } from "@/models/resource-group";
import { useSidebarContext } from "@/hooks/use-sidebar";
import {
  useUpdateResourceGroup,
  useDeleteResourceGroup,
  useCreateResourceGroup,
} from "@/api/resource-group";
import ColorPicker from "./color-picker";
import { ContextMenuItem, ContextMenuDivider } from "./context-menu-helpers";
import { MotionDiv } from "./motion-types";
import logger from "@/utils/logger";
import { Z_INDEX } from "@/utils/z-index";
import { optimisticMutation, urlPredicate } from "@/utils/optimistic";

// ============================================================================
// Types
// ============================================================================

interface ResourceGroupContextMenuProps {
  position: { x: number; y: number };
  group: ResourceGroup;
  resourceType: ResourceType;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ResourceGroupContextMenu component - context menu for resource groups.
 * Allows renaming, changing color, expanding/collapsing, pinning, creating subgroups, and deleting.
 *
 * This is a generic component that works with any resource type (tracker, enrichment, alert, dashboard).
 */
const ResourceGroupContextMenu: React.FC<ResourceGroupContextMenuProps> = ({
  position,
  group,
  resourceType,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const menuRef = useRef<HTMLDivElement>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(group.name || "");
  const renameInputRef = useRef<HTMLInputElement>(null);

  const { pinItem, getItemConfig, toggleItem, expandedItems } =
    useSidebarContext();

  const updateGroupMutation = useUpdateResourceGroup();
  const deleteGroupMutation = useDeleteResourceGroup();
  const createGroupMutation = useCreateResourceGroup();

  const groupColor = group.color || "var(--arc-accent)";
  const itemConfig = getItemConfig(group.id!);
  const isPinned = itemConfig?.pinned || false;
  const isExpanded = expandedItems.includes(group.id!);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isRenaming) {
          setIsRenaming(false);
          setNewName(group.name || "");
        } else {
          onClose();
        }
        return;
      }

      if (isRenaming) {
        if (e.key === "Enter") {
          handleRenameSubmit();
        }
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;

      if (isMeta) {
        switch (e.key.toLowerCase()) {
          case "r":
            e.preventDefault();
            handleRename();
            break;
          case "p":
            e.preventDefault();
            handlePin();
            break;
        }
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        handleDelete();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isRenaming, newName]);

  // Focus rename input when renaming
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 240),
    y: Math.min(position.y, window.innerHeight - 350),
  };

  const groupPredicate = urlPredicate("resource_group");
  const resourcePredicate = urlPredicate(resourceType);

  const invalidateGroups = () => {
    queryClient.invalidateQueries({ predicate: groupPredicate });
    queryClient.invalidateQueries({ predicate: resourcePredicate });
  };

  const handlePin = () => {
    pinItem(group.id!, "group", !isPinned);
    onClose();
  };

  const handleExpand = () => {
    toggleItem(group.id!);
    onClose();
  };

  const handleRename = () => {
    setIsRenaming(true);
  };

  const handleRenameSubmit = async () => {
    if (!newName.trim() || newName === group.name) {
      setIsRenaming(false);
      setNewName(group.name || "");
      return;
    }

    try {
      await optimisticMutation<ResourceGroup[]>({
        queryClient,
        queryPredicate: groupPredicate,
        updater: (groups) =>
          groups.map((g) =>
            g.id === group.id ? { ...g, name: newName.trim() } : g,
          ),
        mutationFn: () =>
          updateGroupMutation.mutateAsync({
            name: newName.trim(),
            uriParams: { id: group.id! },
          }),
        onSettled: () => invalidateGroups(),
      });
    } catch (error) {
      logger.error("Failed to rename group:", error);
    } finally {
      setIsRenaming(false);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteGroupMutation.mutateAsync({
        uriParams: { id: group.id! },
      });

      // Clean up expanded folders state in localStorage
      const storageKeys = [
        "trackerGalleryExpandedFolders",
        "enrichmentGalleryExpandedFolders",
        "alertGalleryExpandedFolders",
        "dashboardGalleryExpandedFolders",
      ];

      storageKeys.forEach((key) => {
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            const expandedFolders = JSON.parse(saved);
            const filtered = expandedFolders.filter(
              (id: string) => id !== group.id,
            );
            localStorage.setItem(key, JSON.stringify(filtered));

            // Dispatch storage event for same-tab synchronization
            window.dispatchEvent(
              new StorageEvent("storage", {
                key,
                newValue: JSON.stringify(filtered),
                url: window.location.href,
                storageArea: localStorage,
              }),
            );
          } catch {
            // Ignore parse errors
          }
        }
      });

      invalidateGroups();
    } catch (error) {
      logger.error("Failed to delete group:", error);
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  const handleChangeColor = async (color: string) => {
    try {
      await optimisticMutation<ResourceGroup[]>({
        queryClient,
        queryPredicate: groupPredicate,
        updater: (groups) =>
          groups.map((g) => (g.id === group.id ? { ...g, color } : g)),
        mutationFn: () =>
          updateGroupMutation.mutateAsync({
            color,
            uriParams: { id: group.id! },
          }),
        onSettled: () => invalidateGroups(),
      });
      setColorPickerOpen(false);
      onClose();
    } catch (error) {
      logger.error("Failed to change group color:", error);
    }
  };

  const handleCreateSubgroup = async () => {
    try {
      await createGroupMutation.mutateAsync({
        name: "Nouveau dossier",
        resource_type: resourceType,
        parent: group.id,
        order: 0,
      });
      invalidateGroups();
      // Expand the parent group to show the new subgroup
      if (!isExpanded) {
        toggleItem(group.id!);
      }
      onClose();
    } catch (error) {
      logger.error("Failed to create subgroup:", error);
    }
  };

  return (
    <>
      <MotionDiv
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className="fixed bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1 min-w-[220px]"
        style={{
          top: adjustedPosition.y,
          left: adjustedPosition.x,
          zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU,
        }}
      >
        {/* Expand/Collapse */}
        <ContextMenuItem onClick={handleExpand}>
          <FaFolderOpen /> {isExpanded ? "Réduire" : "Développer"}
        </ContextMenuItem>

        {/* Pin/Unpin */}
        <ContextMenuItem onClick={handlePin} shortcut="⌘P">
          <FaThumbtack className={isPinned ? "text-yellow-400" : ""} />
          {isPinned ? "Désépingler" : "Épingler aux favoris"}
        </ContextMenuItem>

        {/* Create Subgroup */}
        <ContextMenuItem onClick={handleCreateSubgroup}>
          <FaFolderPlus /> Créer un sous-dossier
        </ContextMenuItem>

        {/* Change color - Submenu */}
        <div className="relative">
          <ContextMenuItem
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
            hasSubmenu
          >
            <FaPalette style={{ color: groupColor }} /> Changer la couleur
          </ContextMenuItem>

          <AnimatePresence>
            {colorPickerOpen && (
              <MotionDiv
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="absolute left-full top-0 ml-1 bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] p-3 min-w-[200px]"
              >
                <ColorPicker
                  value={groupColor}
                  onChange={handleChangeColor}
                  compact
                />
              </MotionDiv>
            )}
          </AnimatePresence>
        </div>

        <ContextMenuDivider />

        {/* Rename */}
        {isRenaming ? (
          <div className="px-3 py-2">
            <input
              ref={renameInputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleRenameSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRenameSubmit();
                } else if (e.key === "Escape") {
                  setIsRenaming(false);
                  setNewName(group.name || "");
                }
              }}
              className="w-full px-2 py-1 text-sm bg-[var(--arc-sidebar-gradient-end)] border border-[var(--arc-sidebar-border-accent)] rounded text-[var(--arc-sidebar-text-primary)] focus:outline-none focus:border-accent"
              placeholder="Nom du dossier"
            />
          </div>
        ) : (
          <ContextMenuItem onClick={handleRename} shortcut="⌘R">
            <FaEdit /> Renommer
          </ContextMenuItem>
        )}

        <ContextMenuDivider />

        {/* Delete */}
        <ContextMenuItem
          onClick={handleDelete}
          shortcut="⌫"
          danger
          disabled={isDeleting}
        >
          <FaTrash /> {isDeleting ? "Suppression..." : "Supprimer"}
        </ContextMenuItem>
      </MotionDiv>
    </>
  );
};

ResourceGroupContextMenu.displayName = "ResourceGroupContextMenu";

export default ResourceGroupContextMenu;
